import * as React from 'react';
import { useFlexLayout, useTable, useFilters, useGlobalFilter } from 'react-table';
import { Col, Row } from '../box/Box';
import { Font } from '../text/font';
import styles from './table.module.scss';
import { CellButton } from './table-cell-components/button/Button';

interface TableProps {
    // We will have to keep adding interfaces for other tables here, or find a way to pass an interface??
    // TODO: Find correct way to setup interface, or just keep any[], if it would be not efficient with many tables
    tableData: any[];

    // This should remain same for all tables
    tableFormat: TableColumnsProps[];

    // Pass other data not included in table data (e.g. button label for Open Maps link)
    otherData?: any;

    // Table Date
    date?: string;

    // Pass filters to the table (can we do this as array later?)
    filterType?: string;
    filterLocation?: string;
    filterRace?: string;
    filters?: FiltersProps[];

    // Variants
    rankingsRow?: boolean;
}

export interface TableColumnsProps {
    Header: string;
    accessor: string;
    width?: number;
    cssPostFix?: string;
    unit?: string;
}

interface TableRowProps {
    row: any;
    otherData?: {
        itineraryButtonLabel?: string;
        performanceButtonLabel?: string;
    };
    rankingRow?: boolean;
    tableFormat: TableColumnsProps[];
}

interface FiltersProps {
    label: string;
    data: string;
}

const TableRow = (props: TableRowProps) => {
    const { otherData, row, rankingRow = false, tableFormat } = props;

    // Mobile
    const [collapsibleVisible, setCollapsibleVisible] = React.useState(false);
    const [rankingsOn, setRankingsOn] = React.useState(false);

    React.useEffect(() => {
        rankingRow ? setRankingsOn(true) : setRankingsOn(false);
    }, ['']);

    const collapsibleButtonCSS = [styles.table_tr_button, rankingsOn ? styles.table_tr_button_rankings : ''].filter(Boolean).join(' ');
    const collapsedDataCSS = [styles.table_tr_collapsed_data, rankingsOn ? styles.table_tr_collapsed_data_rankings : ''].filter(Boolean).join(' ');

    return (
        <div
            {...row.getRowProps()}
            className={styles.table_tr_data}
            onClick={(): void => {
                collapsibleVisible ? setCollapsibleVisible(false) : setCollapsibleVisible(true);
            }}
        >
            <Row className={styles.table_tr_visible_data}>
                {row.cells.map((cell: any, index: any) => {
                    const findColumn = tableFormat.find((item) => item.Header === cell.column.Header);

                    let cellCssClass: string | undefined = 'none';

                    if (findColumn) {
                        cellCssClass = findColumn.hasOwnProperty('cssPostFix') ? findColumn.cssPostFix : findColumn.Header;
                    }

                    const cellUnit = findColumn ? (findColumn.hasOwnProperty('unit') ? findColumn.unit : undefined) : undefined;

                    return cell.column.Header == 'Itinerary' || cell.column.Header == 'Performance' ? (
                        <div key={index} {...cell.getCellProps()} className={[styles.table_td, `table_fix_${cellCssClass}`].filter(Boolean).join(' ')}>
                            <CellButton cell={cell} otherData={otherData}></CellButton>
                        </div>
                    ) : cell.column.Header == 'Country' || cell.column.Header == 'Nat.' ? (
                        <div key={index} {...cell.getCellProps()} className={[styles.table_td, `table_fix_${cellCssClass}`].filter(Boolean).join(' ')}>
                            <div className={styles.table_flag}>
                                <span className={['fi', `fi-${cell.value}`].filter(Boolean).join(' ')}></span>
                            </div>
                        </div>
                    ) : (
                        <div key={index} {...cell.getCellProps()} className={[styles.table_td, `table_fix_${cellCssClass}`].filter(Boolean).join(' ')}>
                            <Font.OxaniumLight mobile="12" desktop="14" className="lh-32 lh-d-28 ls-05">
                                {cell.render('Cell')}
                                {cellUnit ? cellUnit : null}
                            </Font.OxaniumLight>
                        </div>
                    );
                })}

                {/* Show collapsed data */}
                <button
                    className={collapsibleButtonCSS}
                    onClick={(): void => {
                        collapsibleVisible ? setCollapsibleVisible(false) : setCollapsibleVisible(true);
                    }}
                >
                    <svg width="33" height="32" focusable="false" aria-hidden="true">
                        <path
                            d="M3.968,12.061C1.775,12.061,0,13.835,0,16.027c0,2.192,1.773,3.967,3.968,3.967c2.189,0,3.966-1.772,3.966-3.967   C7.934,13.835,6.157,12.061,3.968,12.061z M16.233,12.061c-2.188,0-3.968,1.773-3.968,3.965c0,2.192,1.778,3.967,3.968,3.967   s3.97-1.772,3.97-3.967C20.201,13.835,18.423,12.061,16.233,12.061z M28.09,12.061c-2.192,0-3.969,1.774-3.969,3.967   c0,2.19,1.774,3.965,3.969,3.965c2.188,0,3.965-1.772,3.965-3.965S30.278,12.061,28.09,12.061z"
                            fill="currentColor"
                            fillRule="evenodd"
                        />
                    </svg>
                </button>
            </Row>

            {collapsibleVisible && (
                <div className={collapsedDataCSS}>
                    {row.cells.map((cell: any, index: any) => {
                        const findColumn = tableFormat.find((item) => item.Header === cell.column.Header);

                        let headerCssClassCollapsible: string | undefined = 'none';

                        if (findColumn) {
                            headerCssClassCollapsible = findColumn.hasOwnProperty('cssPostFix') ? findColumn.cssPostFix : findColumn.Header;
                        }

                        // Columns that don't ever get shown in the collapsible
                        return cell.column.Header == 'Hour' ||
                            cell.column.Header == 'Event Type' ||
                            cell.column.Header == 'Date' ||
                            cell.column.cssPostFix == 'RaceRank' ||
                            cell.column.Header == 'Name' ? null : cell.column.Header == 'Itinerary' || cell.column.Header == 'Performance' ? ( // Variant - Buttons
                            <div key={index} className={[styles.table_tr_cd_item, `table_tr_cd_item_${headerCssClassCollapsible}`].filter(Boolean).join(' ')}>
                                <CellButton cell={cell} otherData={otherData}></CellButton>
                            </div>
                        ) : // Variant - Flags
                        cell.column.Header == 'Country' || cell.column.Header == 'Nat.' ? (
                            <div key={index} className={[styles.table_tr_cd_item, `table_tr_cd_item_${headerCssClassCollapsible}`].filter(Boolean).join(' ')}>
                                <Font.OxaniumBold mobile="12" desktop="14" className="lh-20 ls-1">
                                    {cell.column.Header}
                                </Font.OxaniumBold>
                                <div className={styles.table_flag}>
                                    <span className={['fi', `fi-${cell.value}`].filter(Boolean).join(' ')}></span>
                                </div>
                            </div>
                        ) : (
                            // Default Variant
                            <div key={index} className={[styles.table_tr_cd_item, `table_tr_cd_item_${headerCssClassCollapsible}`].filter(Boolean).join(' ')}>
                                <Font.OxaniumBold mobile="12" desktop="14" className="lh-20 ls-1">
                                    {cell.column.Header}
                                </Font.OxaniumBold>
                                <Font.OxaniumLight mobile="12" desktop="14" className="lh-24 ls-05">
                                    {cell.render('Cell')}
                                </Font.OxaniumLight>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const Table = (props: TableProps) => {
    const { tableFormat, tableData, otherData, date, filters, rankingsRow = false } = props;

    // Memoize passed data
    const data = React.useMemo(() => tableData, []);

    // Memoize passed columns format
    const columns = React.useMemo(() => tableFormat, []);

    // Default Column - Usable with useFlexLayout
    const defaultColumn = React.useMemo(
        () => ({
            width: 33,
            Filter: () => null,
        }),
        []
    );

    // Create table instance
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setFilter } = useTable(
        {
            // Pass memoized columns format
            columns,

            // Pass memoized data that MUST match the columns format
            data,

            // Here we need to hide columns that are used only for filtering
            initialState: { hiddenColumns: ['type', 'race'] },

            // Pass default column properties
            defaultColumn,
        },
        useFlexLayout,
        useFilters, // useFilters!
        useGlobalFilter // useGlobalFilter!
    );

    React.useEffect(() => {
        filters?.map((filter: FiltersProps) => {
            setFilter(filter.label, filter.data);
        });
    }, [filters]);

    // Render the whole table ONLY if we have at least one row
    return rows.length == 0 ? null : (
        <React.Fragment>
            {/* Date Heading */}
            {date && (
                <Col className={styles.race_agenda_list_item}>
                    <Font.OxaniumBold className={styles.list_item_date}>{date}</Font.OxaniumBold>
                </Col>
            )}

            {/* Table */}
            <div {...getTableProps()} className={styles.table}>
                <div>
                    {rows &&
                        headerGroups.map((headerGroup, idx: number) => (
                            // Table Header Row
                            <div {...headerGroup.getHeaderGroupProps()} className={styles.table_tr} key={idx}>
                                {headerGroup.headers.map((column, index) => {
                                    const findColumn = tableFormat.find((item) => item.Header === column.render('Header'));

                                    let headerCssClass: string | undefined = 'none';

                                    if (findColumn) {
                                        headerCssClass = findColumn.hasOwnProperty('cssPostFix') ? findColumn.cssPostFix : findColumn.Header;
                                    }

                                    return (
                                        <div
                                            {...column.getHeaderProps()}
                                            key={index}
                                            // It hides table_fix_[column.Header] for those that are defined to be hidden on mobile in table-mobile-styles.modules.scss
                                            className={[styles.table_th, `table_fix_${headerCssClass}`].filter(Boolean).join(' ')}
                                        >
                                            <Font.OxaniumBold mobile="14" desktop="16" className="lh-28 lh-d-32 ls-1">
                                                {column.render('Header')}
                                            </Font.OxaniumBold>
                                        </div>
                                    );
                                })}
                            </div>
                        ))}
                </div>
                {/* Table Body */}
                <div className={styles.table_body} {...getTableBodyProps()}>
                    {/* Rendeer table body only if we have rows */}
                    {rows?.map((row, idx: number) => {
                        prepareRow(row);

                        return otherData ? (
                            rankingsRow ? (
                                <TableRow row={row} otherData={otherData} rankingRow={rankingsRow} tableFormat={tableFormat} key={idx}></TableRow>
                            ) : (
                                <TableRow row={row} otherData={otherData} tableFormat={tableFormat} key={idx}></TableRow>
                            )
                        ) : rankingsRow ? (
                            <TableRow row={row} rankingRow={rankingsRow} tableFormat={tableFormat} key={idx}></TableRow>
                        ) : (
                            <TableRow row={row} tableFormat={tableFormat} key={idx}></TableRow>
                        );
                    })}
                </div>
            </div>
        </React.Fragment>
    );
};
